import { FC, useContext, useEffect, useMemo, useState } from 'react';
import { IEventMetadata, IFormFieldMetadata, IFormMetadata, IFormSectionMetadata, IFormVersionMetadata, ISourceData, ISourceDataConfiguration, ISourceDataConfigurationEvent, ISourceDataConfigurationForm, ISourceDataConfigurationFormSection, ISourceDataConfigurationFormVersion, ISourceDataEvent, ISourceDataField, ISourceDataForm, ITrialFormMetadata } from '@/api/dtos';
import { BasicAlert, CollapsiblePanel, ICollapsiblePanelComponentProps, Markdown } from '@ngt/forms';
import { alpha, Box, Button, Chip, Divider, styled, Tooltip, Typography } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarDays, faCodeBranch, faRepeat, faSquareList } from '@fortawesome/duotone-light-svg-icons';
import { faCheckCircle, faExclamationCircle, faExclamationTriangle, faInfoCircle } from '@fortawesome/pro-light-svg-icons';
import { ISourceDataFormFormComponentProps } from '@/utilities/forms/SourceDataFormProcessor';
import SourceDataExpandingContext from '@/contexts/forms/SourceDataExpandingContext';
import useSourceDataFormReviewCount from '@/hooks/form/useSourceDataFormReviewCount';

const CollapsiblePanelTitle = styled(Button)(({ theme }) => ({
    textTransform: 'none',
    justifyContent: 'flex-start',
    borderRadius: 0,
    minHeight: 74
}));

const IconContainer = styled(Box)(({ theme }) => ({
    marginRight: theme.spacing(2),
    display: 'inline-block'
}));

const TitleChip = styled(Chip)(({ theme }) => ({
    marginLeft: 'auto'
}));

const RepeatChip = styled(Chip)(({ auto, theme }: any) => ({
    marginLeft: auto ? 'auto' : theme.spacing(1)
}));

const SourceDataFormContainer = styled(Box)(({ theme }) => ({
    marginLeft: theme.spacing(8)
}));

const CountChip = styled(Chip)(({ auto, theme }: any) => ({
    marginLeft: auto ? 'auto' : theme.spacing(1),
    minWidth: 110
}));

const CountFlex = styled(Box)(({ auto, theme }: any) => ({
    display: 'flex'
}));

const Count = styled(Box)(({ auto, theme }: any) => ({
    minWidth: 50
}));

const SourceDataFormFormExpanding: FC<ISourceDataFormFormComponentProps> = ({
    settings,
    children,
    ...form
}) => {
    const {
        formLabel,
        formVersionLabel,
        formVersionEnabled,
        formIdentifier,
        formSourceData,
        formPropertyPath,
        formConfiguration,
    } = form;
    
    const hasForm = !!formSourceData;

    const disabled = !hasForm || !formVersionEnabled;

    if (!hasForm && settings?.hideForms) {
        return null;
    }

    if (hasForm && !formVersionEnabled && settings?.hideVersions) {
        return null;
    }

    const { expanded, toggleExpanding } = useContext(SourceDataExpandingContext);

    const counts = useSourceDataFormReviewCount(formPropertyPath!, form)

    return (
        <SourceDataFormContainer>
            <CollapsiblePanelTitle 
                variant='contained' 
                color="secondary"
                fullWidth
                onClick={() => toggleExpanding(formIdentifier!)}
                disabled={disabled}
            >
                <Typography variant="h3">
                    <IconContainer>
                        <FontAwesomeIcon icon={faSquareList} />
                    </IconContainer>
                    {formLabel}
                </Typography>
                {
                    !hasForm && (
                        <TitleChip
                            label="No Recorded Form"
                        />
                    )
                }
                {
                    hasForm && !formVersionEnabled && (
                        <TitleChip
                            label="Version Missing Configuration"
                        />
                    )
                }
                {
                    formVersionLabel && (
                        <Tooltip title="Version">
                            <RepeatChip
                                // @ts-ignore
                                auto={!disabled ? "1" : undefined}
                                icon={<FontAwesomeIcon icon={faCodeBranch} />}
                                label={formVersionLabel}
                            />
                        </Tooltip>
                    )
                }
                {
                    counts && (
                        <Tooltip title="Fields Pending Review">
                            <CountChip
                                // @ts-ignore
                                auto={!formVersionLabel && !disabled ? "1" : undefined}
                                label={
                                    <CountFlex>
                                        <Count>
                                            {counts.reviewed}
                                        </Count>
                                        /
                                        <Count>
                                            {counts.total}
                                        </Count>
                                    </CountFlex>
                                }
                                color={counts.reviewed !== counts.total ? 'error' : 'success'}
                            />
                        </Tooltip>
                    )
                }
            </CollapsiblePanelTitle>
            {
                expanded[formIdentifier!] && (
                    <>
                        { 
                            !!formConfiguration?.alertInformation && (
                                <BasicAlert
                                    description={<Markdown.Preview>{formConfiguration.alertInformation}</Markdown.Preview>}
                                    severity="info"
                                    icon={faInfoCircle}
                                />
                            )
                        }
                        { 
                            !!formConfiguration?.alertWarning && (
                                <BasicAlert
                                    description={<Markdown.Preview>{formConfiguration.alertWarning}</Markdown.Preview>}
                                    severity="warning"
                                    icon={faExclamationCircle}
                                />
                            )
                        }
                        { 
                            !!formConfiguration?.alertError && (
                                <BasicAlert
                                    description={<Markdown.Preview>{formConfiguration.alertError}</Markdown.Preview>}
                                    severity="error"
                                    icon={faExclamationTriangle}
                                />
                            )
                        }
                        { 
                            !!formConfiguration?.alertSuccess && (
                                <BasicAlert
                                    description={<Markdown.Preview>{formConfiguration.alertSuccess}</Markdown.Preview>}
                                    severity="success"
                                    icon={faCheckCircle}
                                />
                            )
                        }
                    </>
                )
            }
            <Divider />
        </SourceDataFormContainer>
    )
}

export default SourceDataFormFormExpanding;